import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './PrettyDateInput.module.css';

interface PrettyDateInputProps {
  value: string;
  onChange: (value: string) => void;
  ariaLabel?: string;
  placeholder?: string;
  minYear?: number;
  maxYear?: number;
}

const MONTHS_KEY = 'long' as const;

export function PrettyDateInput({
  value,
  onChange,
  ariaLabel,
  placeholder,
  minYear = 1900,
  maxYear,
}: PrettyDateInputProps): JSX.Element {
  const { i18n, t } = useTranslation('flows');
  const today = useMemo(() => new Date(), []);
  const effectiveMaxYear = maxYear ?? today.getFullYear();

  const initial = parseIso(value) ?? new Date(today.getFullYear() - 25, today.getMonth(), 1);
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState<number>(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState<number>(initial.getMonth());
  const [pickerMode, setPickerMode] = useState<'days' | 'months' | 'years'>('days');

  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const formatted = formatPretty(value, i18n.language);
  const label = ariaLabel ?? t('onboarding.birthData.birthDate');
  const empty = !formatted;

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent): void => {
      if (!popoverRef.current) return;
      if (popoverRef.current.contains(e.target as Node)) return;
      if (triggerRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  useEffect(() => {
    const parsed = parseIso(value);
    if (parsed) {
      setViewYear(parsed.getFullYear());
      setViewMonth(parsed.getMonth());
    }
  }, [value]);

  function selectDay(day: number): void {
    const iso = toIso(viewYear, viewMonth, day);
    onChange(iso);
    setOpen(false);
  }

  function shiftMonth(delta: number): void {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  }

  function selectMonth(m: number): void {
    setViewMonth(m);
    setPickerMode('days');
  }

  function selectYear(y: number): void {
    setViewYear(y);
    setPickerMode('months');
  }

  const monthName = new Intl.DateTimeFormat(i18n.language, {
    month: MONTHS_KEY,
    year: 'numeric',
  }).format(new Date(viewYear, viewMonth, 1));

  const weekdayShort = useMemo(() => {
    // Monday-first short labels in current locale
    const base = new Date(2024, 0, 1); // Monday
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      return new Intl.DateTimeFormat(i18n.language, { weekday: 'narrow' }).format(d);
    });
  }, [i18n.language]);

  const monthCells = useMemo(() => buildMonthCells(viewYear, viewMonth), [viewYear, viewMonth]);

  const selectedIso = value;

  const yearGridStart = Math.floor(viewYear / 12) * 12;
  const yearGrid = Array.from({ length: 12 }, (_, i) => yearGridStart + i)
    .filter((y) => y >= minYear && y <= effectiveMaxYear);

  return (
    <div className={styles.wrap}>
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.display} ${empty ? styles.displayEmpty : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className={styles.calIcon} aria-hidden="true">▤</span>
        <span className={styles.displayText}>{formatted || placeholder || '—'}</span>
        <span className={styles.chev} aria-hidden="true">▾</span>
      </button>

      {open && (
        <div
          ref={popoverRef}
          className={styles.popover}
          role="dialog"
          aria-label={label}
        >
          <header className={styles.popHeader}>
            <button
              type="button"
              className={styles.modeBtn}
              onClick={() => setPickerMode(pickerMode === 'days' ? 'years' : 'days')}
              aria-label="Toggle picker mode"
            >
              {pickerMode === 'years'
                ? `${yearGridStart} – ${yearGridStart + 11}`
                : pickerMode === 'months'
                  ? String(viewYear)
                  : monthName}
              <span className={styles.modeChev} aria-hidden="true">▾</span>
            </button>
            <div className={styles.navGroup}>
              <button
                type="button"
                className={styles.navBtn}
                onClick={() => {
                  if (pickerMode === 'years') {
                    setViewYear(viewYear - 12);
                  } else if (pickerMode === 'months') {
                    setViewYear(viewYear - 1);
                  } else {
                    shiftMonth(-1);
                  }
                }}
                aria-label="Previous"
              >
                ‹
              </button>
              <button
                type="button"
                className={styles.navBtn}
                onClick={() => {
                  if (pickerMode === 'years') {
                    setViewYear(viewYear + 12);
                  } else if (pickerMode === 'months') {
                    setViewYear(viewYear + 1);
                  } else {
                    shiftMonth(1);
                  }
                }}
                aria-label="Next"
              >
                ›
              </button>
            </div>
          </header>

          {pickerMode === 'days' && (
            <>
              <div className={styles.weekHead} aria-hidden="true">
                {weekdayShort.map((w, i) => (
                  <span key={i} className={styles.weekCell}>{w}</span>
                ))}
              </div>
              <div className={styles.grid}>
                {monthCells.map((cell, i) => {
                  if (!cell) return <span key={`e-${i}`} className={styles.cellEmpty} />;
                  const iso = toIso(cell.year, cell.month, cell.day);
                  const isSelected = iso === selectedIso;
                  const isToday =
                    cell.year === today.getFullYear() &&
                    cell.month === today.getMonth() &&
                    cell.day === today.getDate();
                  const isFuture = new Date(cell.year, cell.month, cell.day) > today;
                  return (
                    <button
                      key={iso}
                      type="button"
                      className={`${styles.dayBtn} ${isSelected ? styles.daySelected : ''} ${isToday ? styles.dayToday : ''}`}
                      onClick={() => selectDay(cell.day)}
                      disabled={isFuture}
                      aria-pressed={isSelected}
                    >
                      {cell.day}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {pickerMode === 'months' && (
            <div className={styles.monthsGrid}>
              {Array.from({ length: 12 }, (_, m) => {
                const name = new Intl.DateTimeFormat(i18n.language, { month: 'short' })
                  .format(new Date(viewYear, m, 1));
                const isCurrent = m === viewMonth;
                return (
                  <button
                    key={m}
                    type="button"
                    className={`${styles.monthBtn} ${isCurrent ? styles.monthSelected : ''}`}
                    onClick={() => selectMonth(m)}
                  >
                    {name}
                  </button>
                );
              })}
            </div>
          )}

          {pickerMode === 'years' && (
            <div className={styles.yearsGrid}>
              {yearGrid.map((y) => {
                const isCurrent = y === viewYear;
                return (
                  <button
                    key={y}
                    type="button"
                    className={`${styles.yearBtn} ${isCurrent ? styles.yearSelected : ''}`}
                    onClick={() => selectYear(y)}
                  >
                    {y}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function parseIso(iso: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [y, m, d] = iso.split('-').map((n) => parseInt(n, 10));
  return new Date(y, m - 1, d);
}

function toIso(year: number, month: number, day: number): string {
  const mm = String(month + 1).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

type Cell = { year: number; month: number; day: number } | null;

function buildMonthCells(year: number, month: number): Cell[] {
  // Monday-first grid
  const first = new Date(year, month, 1);
  const dow = (first.getDay() + 6) % 7; // 0=Mon..6=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: Cell[] = [];
  for (let i = 0; i < dow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push({ year, month, day: d });
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function formatPretty(iso: string, locale: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  const [y, m, d] = iso.split('-').map((n) => parseInt(n, 10));
  const date = new Date(Date.UTC(y, m - 1, d));
  try {
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(date);
  } catch {
    return iso;
  }
}
